/**
 * Sidebar Navigation Component
 * Responsive: temporary drawer on mobile, persistent on desktop
 * Optimized with memoization for better performance
 */

import React, { useCallback, useMemo, memo } from "react";
import {
  Collapse,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  Avatar,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  FamilyRestroom as FamilyIcon,
  Class as ClassIcon,
  Assignment as ApplicationIcon,
  AssignmentInd as EnrollmentIcon,
  LocationOn as CampusIcon,
  Receipt as ReceiptIcon,
  RequestQuote as FeeTypeIcon,
  ChevronLeft as ChevronLeftIcon,
  BusinessCenter as TenantIcon,
  History as AuditIcon,
  Payment as PaymentIcon,
  Assessment as ReportsIcon,
  Insights as AnalyticsIcon,
  LocalOffer as DiscountIcon,
  VolunteerActivism as ZakatFundIcon,
  Campaign as CampaignIcon,
  Restaurant as PotluckIcon,
  Description as StatementIcon,
  PlaylistAdd as BatchIcon,
  EventNote as AttendanceIcon,
  CreditCard as CreditCardIcon,
  Person as ProfileIcon,
  Badge as TeacherIcon,
  Notifications as NotificationsIcon,
  MenuBook as HifdhIcon,
  Checklist as LessonProgressIcon,
  CalendarToday as CalendarIcon,
  AutoStories as SubjectsIcon,
  Stairs as GradeLevelsIcon,
  LibraryBooks as CurriculumIcon,
  AccessTime as TimeclockIcon,
  AdminPanelSettings as PlatformAdminIcon,
  PriceCheck as PricingIcon,
  Settings as SettingsIcon,
  HelpOutlined as HelpIcon,
  Mood as BehaviorIcon,
  Forum as MessagesIcon,
  Article as ReportCardsIcon,
  AssignmentTurnedIn as AssignmentsIcon,
  EditCalendar as LessonPlansIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { useUIStore } from "@/store/uiStore";
import { useCurrentTenant } from "@/hooks/useTenants";

const DRAWER_WIDTH = 280;

export interface NavItem {
  title: string;
  icon: React.ReactElement;
  path: string;
  roles?: string[];
  permission?: string;
  /** If true, only show for Islamic schools */
  islamicOnly?: boolean;
  /** If true, only show when the tenant has sponsor funding enabled (mig 163) */
  sponsorFundingOnly?: boolean;
  /**
   * Where this item sits in the menu.
   *
   * REQUIRED: it used to be optional and to mean "start a new section here",
   * so an item without one silently joined whatever run preceded it. That is
   * how community activities came to sit inside Billing, and how Help Center
   * came to sit under whichever group happened to precede it for that user's
   * role. Declaring it per item makes a new entry's placement a decision
   * rather than an accident.
   */
  section: NavPlacement;
}

/** Every group that gets a header of its own. */
type NavSection =
  | "People"
  | "Academics"
  | "Community"
  | "Billing"
  | "Operations"
  | "Insights"
  | "Admin";

/**
 * A group, or one of the two header-less runs: "top" (Dashboard) and "bottom"
 * (Help Center). These are distinct values rather than one shared null so
 * that an item belonging at the end does not have to claim membership of a
 * group it has nothing to do with — Help Center was showing under "ADMIN",
 * and to a staff user it was the group's only entry.
 */
type NavPlacement = NavSection | "top" | "bottom";

/**
 * Groups in the order they appear, with null the ungrouped run at the top.
 *
 * This, not the order of the arrays below, is what the user sees. It is a
 * Record rather than an array so that adding a group to NavSection without
 * placing it here is a compile error instead of items silently vanishing; the
 * key order is the render order.
 */
const SECTION_RENDER_ORDER: Record<NavSection, true> = {
  People: true,
  Academics: true,
  Community: true,
  Billing: true,
  Operations: true,
  Insights: true,
  Admin: true,
};
const SECTION_ORDER: NavPlacement[] = [
  "top",
  ...(Object.keys(SECTION_RENDER_ORDER) as NavSection[]),
  "bottom",
];

/** A rendered run of nav items: a named, collapsible group or a header-less run. */
interface NavGroup {
  key: NavPlacement;
  name: NavSection | null;
  items: NavItem[];
}

/**
 * Bucket nav items by the placement each one DECLARES, then emit the buckets in
 * SECTION_ORDER.
 *
 * Deliberately not "merge consecutive items that share a section": that is
 * only correct while the arrays below happen to be sorted, and an item added
 * in the wrong place would render a second header with the same name — and so
 * a duplicate React key and a collapse state shared with the first. Exported
 * for the test that pins exactly that.
 */
export function groupNavItemsBySection(
  items: NavItem[]
): NavGroup[] {
  const byPlacement = new Map<NavPlacement, NavItem[]>();
  for (const item of items) {
    const bucket = byPlacement.get(item.section);
    if (bucket) bucket.push(item);
    else byPlacement.set(item.section, [item]);
  }

  const groups: NavGroup[] = [];
  for (const placement of SECTION_ORDER) {
    const bucket = byPlacement.get(placement);
    if (!bucket?.length) continue;
    groups.push({
      key: placement,
      // "top" and "bottom" render without a header.
      name: placement === "top" || placement === "bottom" ? null : placement,
      items: bucket,
    });
  }
  return groups;
}

// Admin/Staff navigation items with section grouping
export const adminNavItems: NavItem[] = [
  {
    title: "Dashboard",
    icon: <DashboardIcon />,
    path: "/dashboard",
    // Admin metrics API (admin/principal). Staff home is Attendance.
    permission: "view:school-metrics",
    section: "top",
  },
  {
    title: "Families",
    icon: <FamilyIcon />,
    path: "/families",
    permission: "manage:families",
    section: "People",
  },
  {
    title: "Students",
    icon: <SchoolIcon />,
    path: "/students",
    permission: "manage:students",
    section: "People",
  },
  {
    title: "Applications",
    icon: <ApplicationIcon />,
    path: "/applications",
    permission: "manage:applications",
    section: "People",
  },
  {
    title: "Teachers",
    icon: <TeacherIcon />,
    path: "/teachers",
    permission: "manage:teachers",
    section: "People",
  },
  {
    title: "Classes",
    icon: <ClassIcon />,
    path: "/classes",
    permission: "manage:classes",
    section: "Academics",
  },
  {
    title: "Enrollments",
    icon: <EnrollmentIcon />,
    path: "/enrollments",
    permission: "manage:enrollments",
    section: "Academics",
  },
  {
    title: "Curriculum",
    icon: <CurriculumIcon />,
    path: "/curriculum",
    roles: ["super_user", "admin", "principal"],
    section: "Academics",
  },
  {
    title: "Subjects",
    icon: <SubjectsIcon />,
    path: "/curriculum/subjects",
    roles: ["super_user", "admin", "principal"],
    section: "Academics",
  },
  {
    title: "Grade Levels",
    icon: <GradeLevelsIcon />,
    path: "/curriculum/grade-levels",
    roles: ["super_user", "admin", "principal"],
    section: "Academics",
  },
  {
    title: "Lesson Plans",
    icon: <LessonPlansIcon />,
    path: "/teacher/lesson-plans",
    roles: ["super_user", "admin", "principal", "staff"],
    section: "Academics",
  },
  {
    title: "Lesson Progress",
    icon: <LessonProgressIcon />,
    path: "/lesson-progress",
    roles: ["super_user", "admin", "principal", "staff", "teacher"],
    section: "Academics",
  },
  {
    title: "Hifdh Progress",
    icon: <HifdhIcon />,
    path: "/hifdh/v2",
    roles: ["super_user", "admin", "principal", "staff", "teacher"],
    islamicOnly: true,
    section: "Academics",
  },
  {
    title: "Assignments",
    icon: <AssignmentsIcon />,
    path: "/assignments",
    roles: ["super_user", "admin", "principal", "staff", "teacher"],
    section: "Academics",
  },
  {
    title: "Report Cards",
    icon: <ReportsIcon />,
    path: "/reports/report-cards",
    roles: ["super_user", "admin", "principal", "staff"],
    section: "Academics",
  },
  {
    title: "Behavior",
    icon: <BehaviorIcon />,
    path: "/behavior",
    roles: ["super_user", "admin", "principal", "staff", "teacher"],
    section: "Academics",
  },
  {
    title: "Messages",
    icon: <MessagesIcon />,
    path: "/messages",
    section: "Community",
  },
  {
    title: "Campaigns",
    icon: <CampaignIcon />,
    path: "/campaigns",
    roles: ["super_user", "admin", "principal"],
    section: "Community",
  },
  {
    title: "Potlucks",
    icon: <PotluckIcon />,
    path: "/potlucks",
    roles: ["super_user", "admin", "principal"],
    section: "Community",
  },
  {
    title: "Fee Types",
    icon: <FeeTypeIcon />,
    path: "/fees/types",
    permission: "manage:fees",
    section: "Billing",
  },
  {
    title: "Invoices",
    icon: <ReceiptIcon />,
    path: "/invoices",
    permission: "manage:fees",
    section: "Billing",
  },
  {
    title: "Payments",
    icon: <PaymentIcon />,
    path: "/payments",
    permission: "manage:fees",
    section: "Billing",
  },
  {
    title: "Discounts",
    icon: <DiscountIcon />,
    path: "/discounts",
    permission: "manage:fees",
    section: "Billing",
  },
  {
    title: "Zakat Fund",
    icon: <ZakatFundIcon />,
    path: "/zakat-fund",
    roles: ["super_user", "admin", "principal"],
    section: "Billing",
  },
  {
    title: "Sponsor Fund",
    icon: <ZakatFundIcon />,
    path: "/sponsor-fund",
    roles: ["super_user", "admin", "principal"],
    sponsorFundingOnly: true,
    section: "Billing",
  },
  {
    title: "Statements",
    icon: <StatementIcon />,
    path: "/statements",
    roles: ["super_user", "admin"],
    section: "Billing",
  },
  {
    title: "Batch Generation",
    icon: <BatchIcon />,
    path: "/invoices/batch",
    roles: ["super_user", "admin"],
    section: "Billing",
  },
  {
    title: "Attendance",
    icon: <AttendanceIcon />,
    path: "/attendance",
    roles: ["super_user", "admin", "principal", "staff", "teacher"],
    section: "Operations",
  },
  {
    title: "Time Clock",
    icon: <TimeclockIcon />,
    path: "/timecard",
    roles: ["super_user", "admin", "principal", "staff", "teacher"],
    section: "Operations",
  },
  {
    title: "Campuses",
    icon: <CampusIcon />,
    path: "/campuses",
    permission: "manage:campuses",
    section: "Operations",
  },
  {
    title: "School Calendar",
    icon: <CalendarIcon />,
    path: "/calendar",
    roles: ["super_user", "admin", "principal"],
    section: "Operations",
  },
  {
    title: "Analytics",
    icon: <AnalyticsIcon />,
    path: "/analytics",
    roles: ["super_user", "admin", "principal"],
    section: "Insights",
  },
  {
    title: "Reports",
    icon: <ReportsIcon />,
    path: "/reports",
    roles: ["super_user", "admin", "principal"],
    section: "Insights",
  },
  {
    title: "Users",
    icon: <PeopleIcon />,
    path: "/users",
    permission: "manage:users",
    section: "Admin",
  },
  {
    title: "Notifications",
    icon: <NotificationsIcon />,
    path: "/notifications/queue",
    roles: ["super_user", "admin"],
    section: "Admin",
  },
  {
    title: "Staff Timecard",
    icon: <TimeclockIcon />,
    path: "/admin/timecard",
    roles: ["super_user", "admin", "principal"],
    section: "Admin",
  },
  {
    title: "Audit Log",
    icon: <AuditIcon />,
    path: "/admin/audit-log",
    permission: "view:audit-log",
    section: "Admin",
  },
  {
    title: "School Settings",
    icon: <TenantIcon />,
    path: "/admin/tenant-settings",
    roles: ["super_user", "admin"],
    section: "Admin",
  },
  {
    title: "Help Center",
    icon: <HelpIcon />,
    path: "/help",
    section: "bottom",
  },
];

// Parent Portal navigation items
const parentNavItems: NavItem[] = [
  {
    title: "Dashboard",
    icon: <DashboardIcon />,
    path: "/parent/dashboard",
    roles: ["parent"],
    section: "top",
  },
  {
    title: "My Students",
    icon: <SchoolIcon />,
    path: "/parent/students",
    roles: ["parent"],
    section: "top",
  },
  {
    title: "Attendance",
    icon: <AttendanceIcon />,
    path: "/parent/attendance",
    roles: ["parent"],
    section: "top",
  },
  {
    title: "Lesson Progress",
    icon: <LessonProgressIcon />,
    path: "/parent/lesson-progress",
    roles: ["parent"],
    section: "top",
  },
  {
    title: "Assignments & Grades",
    icon: <AssignmentsIcon />,
    path: "/parent/assignments",
    roles: ["parent"],
    section: "top",
  },
  {
    title: "Performance",
    icon: <AnalyticsIcon />,
    path: "/parent/performance",
    roles: ["parent"],
    section: "top",
  },
  {
    title: "Hifdh Progress",
    icon: <HifdhIcon />,
    path: "/parent/hifdh-progress",
    roles: ["parent"],
    islamicOnly: true,
    section: "top",
  },
  {
    title: "Behavior",
    icon: <BehaviorIcon />,
    path: "/behavior",
    roles: ["parent"],
    section: "top",
  },
  {
    title: "Report Cards",
    icon: <ReportCardsIcon />,
    path: "/reports/report-cards",
    roles: ["parent"],
    section: "top",
  },
  {
    title: "Messages",
    icon: <MessagesIcon />,
    path: "/messages",
    roles: ["parent"],
    section: "top",
  },
  {
    title: "Applications",
    icon: <ApplicationIcon />,
    path: "/parent/applications",
    roles: ["parent"],
    section: "top",
  },
  {
    title: "Invoices",
    icon: <ReceiptIcon />,
    path: "/parent/invoices",
    roles: ["parent"],
    section: "top",
  },
  {
    title: "Payments",
    icon: <PaymentIcon />,
    path: "/parent/payments",
    roles: ["parent"],
    section: "top",
  },
  {
    title: "Statements",
    icon: <StatementIcon />,
    path: "/parent/statements",
    roles: ["parent"],
    section: "top",
  },
  {
    title: "Payment Methods",
    icon: <CreditCardIcon />,
    path: "/parent/payment-methods",
    roles: ["parent"],
    section: "top",
  },
  {
    title: "Profile",
    icon: <ProfileIcon />,
    path: "/parent/profile",
    roles: ["parent"],
    section: "top",
  },
  {
    title: "Help Center",
    icon: <HelpIcon />,
    path: "/help",
    roles: ["parent"],
    section: "bottom",
  },
];

// Teacher Portal navigation items
export const teacherNavItems: NavItem[] = [
  {
    title: "Dashboard",
    icon: <DashboardIcon />,
    path: "/teacher/dashboard",
    roles: ["teacher"],
    section: "top",
  },
  {
    title: "My Classes",
    icon: <ClassIcon />,
    path: "/teacher/classes",
    roles: ["teacher"],
    section: "top",
  },
  {
    title: "My Students",
    icon: <SchoolIcon />,
    path: "/teacher/students",
    roles: ["teacher"],
    section: "top",
  },
  {
    title: "Attendance",
    icon: <AttendanceIcon />,
    path: "/attendance",
    roles: ["teacher"],
    section: "top",
  },
  {
    title: "Curriculum",
    icon: <CurriculumIcon />,
    path: "/curriculum",
    roles: ["teacher"],
    section: "top",
  },
  {
    title: "Subjects",
    icon: <SubjectsIcon />,
    path: "/curriculum/subjects",
    roles: ["teacher"],
    section: "top",
  },
  {
    title: "Hifdh Progress",
    icon: <HifdhIcon />,
    path: "/hifdh/v2",
    roles: ["teacher"],
    islamicOnly: true,
    section: "top",
  },
  {
    title: "Lesson Plans",
    icon: <LessonPlansIcon />,
    path: "/teacher/lesson-plans",
    roles: ["teacher"],
    section: "top",
  },
  {
    title: "Lesson Progress",
    icon: <LessonProgressIcon />,
    path: "/lesson-progress",
    roles: ["teacher"],
    section: "top",
  },
  {
    title: "Assignments",
    icon: <AssignmentsIcon />,
    path: "/assignments",
    roles: ["teacher"],
    section: "top",
  },
  {
    title: "Behavior",
    icon: <BehaviorIcon />,
    path: "/behavior",
    roles: ["teacher"],
    section: "top",
  },
  {
    title: "Report Cards",
    icon: <ReportCardsIcon />,
    path: "/reports/report-cards",
    roles: ["teacher"],
    section: "top",
  },
  {
    title: "Messages",
    icon: <MessagesIcon />,
    path: "/messages",
    roles: ["teacher"],
    section: "top",
  },
  {
    title: "Time Clock",
    icon: <TimeclockIcon />,
    path: "/timecard",
    roles: ["teacher"],
    section: "top",
  },
  {
    title: "Help Center",
    icon: <HelpIcon />,
    path: "/help",
    roles: ["teacher"],
    section: "bottom",
  },
];

// Platform Admin navigation items (super_user only)
// NOTE: rendered by its own block, not through groupNavItemsBySection,
// so `section` on these entries is required by the type and otherwise inert.
const platformAdminNavItems: NavItem[] = [
  {
    title: "Platform Dashboard",
    icon: <PlatformAdminIcon />,
    path: "/platform",
    roles: ["super_user"],
    section: "top",
  },
  {
    title: "All Tenants",
    icon: <TenantIcon />,
    path: "/platform/tenants",
    roles: ["super_user"],
    section: "top",
  },
  {
    title: "Pricing Plans",
    icon: <PricingIcon />,
    path: "/platform/pricing-plans",
    roles: ["super_user"],
    section: "top",
  },
  {
    title: "Platform Invoices",
    icon: <ReceiptIcon />,
    path: "/platform/invoices",
    roles: ["super_user"],
    section: "top",
  },
  {
    title: "Platform Settings",
    icon: <SettingsIcon />,
    path: "/platform/settings",
    roles: ["super_user"],
    section: "top",
  },
];

// Bottom nav items kept for super_user role only (minimal admin tools)
// NOTE: rendered by its own block below the groups, not through groupNavItemsBySection,
// so `section` on these entries is required by the type and otherwise inert.
const bottomNavItems: NavItem[] = [
  {
    title: "Users",
    icon: <PeopleIcon />,
    path: "/users",
    permission: "manage:users",
    section: "top",
  },
  {
    title: "Audit Log",
    icon: <AuditIcon />,
    path: "/admin/audit-log",
    permission: "view:audit-log",
    section: "top",
  },
];

export const Sidebar: React.FC = memo(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  // Selector subscriptions: re-render only on the specific slice that changed,
  // not on every UI-store mutation. (Zustand action refs are stable.)
  const sidebarOpen = useUIStore((s) => s.sidebarOpen);
  const setSidebarOpen = useUIStore((s) => s.setSidebarOpen);
  const mobileNavOpen = useUIStore((s) => s.mobileNavOpen);
  const setMobileNavOpen = useUIStore((s) => s.setMobileNavOpen);
  const collapsedSections = useUIStore((s) => s.collapsedSections);
  const toggleSection = useUIStore((s) => s.toggleSection);
  const { user, hasRole, hasPermission } = useAuthStore();

  // Below md the drawer is a modal that only the menu button opens; at md+ it
  // is the persistent sidebar. Separate flags (MUI's responsive-drawer
  // pattern) so crossing the breakpoint -- rotating a phone or tablet,
  // narrowing a window -- never mounts the modal already open and then closes
  // it, which stranded MUI's aria-hidden on #root and hid the whole app from
  // screen readers.
  const drawerOpen = isMobile ? mobileNavOpen : sidebarOpen;

  // Leaving phone width closes the phone menu, so it is closed on the way back.
  React.useEffect(() => {
    if (!isMobile) setMobileNavOpen(false);
  }, [isMobile, setMobileNavOpen]);

  // Get current tenant to check school_type (skip for super_users who don't have a tenant)
  const { data: tenant } = useCurrentTenant(user?.role === "super_user");
  const isIslamicSchool = tenant?.school_type === "islamic";
  const sponsorFundingEnabled = !!tenant?.settings?.sponsor_funding?.enabled;

  const handleNavigation = useCallback((path: string) => {
    navigate(path);
    // Close the phone menu after navigation
    if (isMobile) {
      setMobileNavOpen(false);
    }
  }, [navigate, isMobile, setMobileNavOpen]);

  const handleCloseSidebar = useCallback(() => {
    if (isMobile) {
      setMobileNavOpen(false);
    } else {
      setSidebarOpen(false);
    }
  }, [isMobile, setMobileNavOpen, setSidebarOpen]);

  const isItemVisible = useCallback((item: NavItem): boolean => {
    if (item.roles && !hasRole(item.roles)) {
      return false;
    }
    if (item.permission && !hasPermission(item.permission)) {
      return false;
    }
    // Hide Islamic-only items (like Hifdh) for non-Islamic schools
    if (item.islamicOnly && !isIslamicSchool) {
      return false;
    }
    // Hide sponsor-funding items (Sponsor Fund) unless the tenant flag is on
    if (item.sponsorFundingOnly && !sponsorFundingEnabled) {
      return false;
    }
    return true;
  }, [hasRole, hasPermission, isIslamicSchool, sponsorFundingEnabled]);

  const isActive = useCallback((path: string): boolean => {
    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  }, [location.pathname]);

  // Memoize visible nav items to avoid recalculating on each render
  const visibleMainNavItems = useMemo(() => {
    // Super users don't see tenant-specific navigation
    if (user?.role === "super_user") {
      return [];
    }
    let navItems = adminNavItems;
    if (user?.role === "parent") {
      navItems = parentNavItems;
    } else if (user?.role === "teacher") {
      navItems = teacherNavItems;
    }
    return navItems.filter(isItemVisible);
  }, [user?.role, isItemVisible]);

  const visibleBottomNavItems = useMemo(() => {
    // Super users only see Users and Audit Log from bottom nav
    if (user?.role === "super_user") {
      return bottomNavItems.filter(
        (item) => item.path === "/users" || item.path === "/admin/audit-log"
      );
    }
    return bottomNavItems.filter(isItemVisible);
  }, [user?.role, isItemVisible]);

  const visiblePlatformAdminNavItems = useMemo(() => {
    return platformAdminNavItems.filter(isItemVisible);
  }, [isItemVisible]);

  // Group visible main nav items by section for collapsible rendering
  const groupedNavSections = useMemo(
    () => groupNavItemsBySection(visibleMainNavItems),
    [visibleMainNavItems]
  );

  // Check if any item in a collapsed section is currently active
  const isSectionActive = useCallback((items: NavItem[]) => {
    return items.some(item => isActive(item.path));
  }, [isActive]);

  const drawerContent = (
    <>
      {/* Header with User Info */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          minHeight: 64,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: "primary.main",
            }}
          >
            {user?.first_name?.[0]}
            {user?.last_name?.[0]}
          </Avatar>
          <Box>
            <Typography variant="subtitle2" sx={{
              fontWeight: 600
            }}>
              {user?.first_name} {user?.last_name}
            </Typography>
            <Typography variant="caption" sx={{
              color: "text.secondary"
            }}>
              {user?.role.replace("_", " ").toUpperCase()}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={handleCloseSidebar}
          size="small"
          aria-label="Close navigation menu"
          sx={{ minWidth: 44, minHeight: 44 }}
        >
          <ChevronLeftIcon />
        </IconButton>
      </Box>

      <Divider />

      {/* Main Navigation Area */}
      <Box sx={{ flexGrow: 1, overflow: "auto", py: 2 }}>
        {/* Platform Admin Navigation (super_user only - shown at top) */}
        {visiblePlatformAdminNavItems.length > 0 && (
          <>
            <Box sx={{ px: 2, pb: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.5
                }}>
                Platform Admin
              </Typography>
            </Box>
            <List component="div" role="group" aria-label="Platform admin navigation" sx={{ pb: 1 }}>
              {visiblePlatformAdminNavItems.map((item) => (
                <ListItem key={item.path} disablePadding sx={{ px: 1 }} component="div">
                  <ListItemButton
                    selected={isActive(item.path)}
                    onClick={() => handleNavigation(item.path)}
                    aria-current={isActive(item.path) ? "page" : undefined}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      minHeight: 44,
                      "&.Mui-selected": {
                        bgcolor: "primary.light",
                        color: "primary.main",
                        fontWeight: 600,
                        "&:hover": {
                          bgcolor: "primary.light",
                        },
                        "& .MuiListItemIcon-root": {
                          color: "primary.main",
                        },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      slotProps={{
                        primary: {
                          sx: {
                            fontSize: 14,
                            fontWeight: isActive(item.path) ? 600 : 400,
                          },
                        }
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            {visibleMainNavItems.length > 0 && <Divider sx={{ my: 1 }} />}
          </>
        )}

        {/* Regular Navigation (tenant-specific) with collapsible sections */}
        {/* No component="nav" here: AppShell wraps the whole drawer in the
            single <nav aria-label="Main navigation"> landmark. This list
            declaring the same landmark and the same name made a screen
            reader announce two, and made the e2e sidebar helper's locator
            match two elements. */}
        {groupedNavSections.length > 0 && (
          <List component="div" sx={{ pt: 0 }}>
            {groupedNavSections.map((section, sectionIndex) => {
              const isCollapsed = section.name ? !!collapsedSections[section.name] : false;
              const hasActiveItem = isSectionActive(section.items);

              return (
                <React.Fragment key={section.key}>
                  {/* The trailing header-less run needs a rule above it. Help
                      Center is not in "Admin" any more, but without this it
                      renders flush under that header at the same indent, so
                      nothing the reader sees says so. */}
                  {section.key === "bottom" && <Divider sx={{ my: 1, mx: 2 }} />}
                  {/* Section header with collapse toggle */}
                  {section.name && (
                    <ListItemButton
                      onClick={() => toggleSection(section.name!)}
                      aria-expanded={!isCollapsed}
                      aria-controls={`nav-section-${section.name}`}
                      sx={{
                        px: 2,
                        pt: sectionIndex === 0 ? 0 : 1,
                        pb: 0.5,
                        minHeight: 32,
                        "&:hover": { bgcolor: "transparent" },
                      }}
                      disableRipple
                    >
                      <Typography
                        variant="caption"
                        color={hasActiveItem && isCollapsed ? "primary.main" : "text.secondary"}
                        sx={{
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          fontSize: "0.68rem",
                          flexGrow: 1
                        }}>
                        {section.name}
                      </Typography>
                      {isCollapsed ? (
                        <ExpandMoreIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                      ) : (
                        <ExpandLessIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                      )}
                    </ListItemButton>
                  )}
                  {/* Section items — collapsible for named sections */}
                  <Collapse
                    in={!isCollapsed}
                    timeout="auto"
                    unmountOnExit={false}
                    id={section.name ? `nav-section-${section.name}` : undefined}
                  >
                    {section.items.map((item) => (
                      <ListItem key={item.path} disablePadding sx={{ px: 1 }} component="div">
                        <ListItemButton
                          selected={isActive(item.path)}
                          onClick={() => handleNavigation(item.path)}
                          aria-current={isActive(item.path) ? "page" : undefined}
                          sx={{
                            borderRadius: 1,
                            mb: 0.5,
                            minHeight: 44,
                            "&.Mui-selected": {
                              bgcolor: "primary.light",
                              color: "primary.main",
                              fontWeight: 600,
                              "&:hover": {
                                bgcolor: "primary.light",
                              },
                              "& .MuiListItemIcon-root": {
                                color: "primary.main",
                              },
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                          <ListItemText
                            primary={item.title}
                            slotProps={{
                              primary: {
                                sx: {
                                  fontSize: 14,
                                  fontWeight: isActive(item.path) ? 600 : 400,
                                },
                              }
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </Collapse>
                </React.Fragment>
              );
            })}
          </List>
        )}

        {/* For super_user: show admin tools inline (no empty space) */}
        {user?.role === "super_user" && visibleBottomNavItems.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <List component="div" role="group" aria-label="Administrative tools" sx={{ pb: 1 }}>
              {visibleBottomNavItems.map((item) => (
                <ListItem key={item.path} disablePadding sx={{ px: 1 }} component="div">
                  <ListItemButton
                    selected={isActive(item.path)}
                    onClick={() => handleNavigation(item.path)}
                    aria-current={isActive(item.path) ? "page" : undefined}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      minHeight: 44,
                      "&.Mui-selected": {
                        bgcolor: "primary.light",
                        color: "primary.main",
                        fontWeight: 600,
                        "&:hover": {
                          bgcolor: "primary.light",
                        },
                        "& .MuiListItemIcon-root": {
                          color: "primary.main",
                        },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      slotProps={{
                        primary: {
                          sx: {
                            fontSize: 14,
                            fontWeight: isActive(item.path) ? 600 : 400,
                          },
                        }
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </Box>

      {/* Bottom nav removed — all items moved into collapsible sections above */}
    </>
  );

  return (
    <Drawer
      variant={isMobile ? "temporary" : "persistent"}
      anchor="left"
      open={drawerOpen}
      onClose={handleCloseSidebar}
      ModalProps={{
        keepMounted: true, // Better mobile performance
        // Never let the drawer scroll-lock the document body. The phone drawer
        // used to mount open and close straight away, and MUI's open-then-
        // immediately-close with keepMounted can fail to RELEASE the body scroll
        // lock (body { overflow: hidden }), leaving pages unscrollable until a
        // re-mount (the "dashboard only scrolls after I navigate away and back"
        // bug on Android Chrome). The separate mobileNavOpen flag now prevents
        // that sequence; keep this as a guard -- the sidebar is a left overlay,
        // so not locking background scroll is fine.
        disableScrollLock: true,
      }}
      sx={{
        width: drawerOpen ? DRAWER_WIDTH : 0,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: isMobile ? "85vw" : DRAWER_WIDTH,
          maxWidth: 320,
          boxSizing: "border-box",
          borderRight: "1px solid",
          borderColor: "divider",
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
});

Sidebar.displayName = "Sidebar";
